/**
 * The ⓘ explanations for "What the tech will do" — plain words, 7th grade,
 * with the real screenshots (client/public/help/tech-plan/*.png) marked up.
 * Positions are % of the picture.
 */
import type { InfoTopic } from "@/components/info-button";

export const TECH_PLAN_HELP: Record<
    "officeBlock" | "preview" | "officeOnly" | "techToday" | "readYourPlan" | "officeReads",
    InfoTopic
> = {
    officeBlock: {
        title: "What the tech will do",
        lines: [
            "This box is the ONE place you tell the tech what to do on this job.",
            "Each device gets one line: what to do to it, and what to bring. The bottom part is for the whole job, like a gate, a call-ahead or an IVR number.",
            "The tech sees these words exactly as you type them. It is the first thing on his iPad.",
            "Notes in the chat are not the place for instructions any more. If the tech must know it, put it here.",
            "With How sure on, each line says where it came from. A gray ✓ means it's the approved quote's line or the office wrote it. ~ means it was copied from old notes, or the device record looks odd: tap it and press It's right, or fix it. ? means a device failed and no line says what to do.",
        ],
        shots: [
            {
                src: "/help/tech-plan/office-block.png",
                alt: "The What the tech will do box on the office job page",
                marks: [
                    { x: 70, y: 7, label: "The tech sees this box word for word", side: "left" },
                    { x: 6, y: 18, label: "One line for each device: Repair, Replace or Test", side: "below" },
                    { x: 23, y: 40, label: "Amber means: fix the device record", side: "below" },
                    { x: 7, y: 51, label: "For the whole job: call-ahead, IVR, gate", side: "above" },
                    { x: 31, y: 87, label: "Edit to change the words", side: "below" },
                ],
            },
        ],
    },
    preview: {
        title: "See what the tech sees",
        lines: [
            "This opens a picture of the tech's iPad, using the same words he will read.",
            "Use it to check the job before it goes out. If it would confuse you, it will confuse him. Fix it with Edit.",
            "Nothing is sent when you open it.",
        ],
        shots: [
            {
                src: "/help/tech-plan/office-preview.png",
                alt: "The tech's iPad preview opened under the box",
                marks: [
                    { x: 23, y: 6, label: "This is the tech's iPad screen", side: "right" },
                    { x: 52, y: 33, label: "Same words as your box", side: "below" },
                    { x: 79, y: 89, label: "He can open the full work order", side: "left" },
                ],
            },
        ],
    },
    officeOnly: {
        title: "🔒 Office only",
        lines: [
            "Things in this gray box are for the office. The tech does NOT see them.",
            "It shows money limits, approval notes and the office's own notes from the work order.",
            "If the tech needs to know something here, type it in the box above instead.",
        ],
        shots: [
            {
                src: "/help/tech-plan/office-only.png",
                alt: "The gray office-only box under the tech's box",
                marks: [{ x: 44, y: 21, label: "Only the office sees this", side: "right" }],
            },
        ],
    },
    techToday: {
        title: "What you'll do today",
        lines: [
            "This is what the office wants done on this job. It comes straight from them.",
            "Each device has one line. Read it before you start. You will see the same line again when you open that device.",
            "“For the whole job” has the call-ahead, gate or IVR numbers.",
            "If it says “Changed by the office”, they updated it. Read it again.",
        ],
        shots: [
            {
                src: "/help/tech-plan/tech-today.png",
                alt: "The What you'll do today box on the iPad",
                marks: [
                    { x: 15, y: 18, label: "What to do to each device", side: "right" },
                    { x: 26, y: 52, label: "Things for the whole job", side: "right" },
                    { x: 75, y: 89, label: "Tap to read the full work order", side: "left" },
                ],
            },
        ],
    },
    readYourPlan: {
        title: "Read your plan",
        lines: [
            "Read the plan above before you leave the shop.",
            "It makes sense: tap Got it. The office sees that you read it.",
            "Something looks wrong (wrong part, wrong device, wrong job): tap This looks wrong, say what, then call the office before you drive out.",
            "If the office changes the plan later, it asks you again.",
        ],
        shots: [
            {
                src: "/help/tech-plan/read-your-plan.png",
                alt: "The plan with Got it and This looks wrong under it",
                marks: [
                    { x: 50, y: 29, label: "Read this first", side: "right" },
                    { x: 50, y: 79, label: "It makes sense: tap Got it", side: "above" },
                    { x: 50, y: 90, label: "Something's off: tap here, then call", side: "below" },
                ],
            },
        ],
    },
    officeReads: {
        title: "Who read the plan",
        lines: [
            "Green: the tech tapped Got it on his iPad, and when.",
            "Red: the tech tapped This looks wrong. His words are under it, and a task is on the Office list. Fix the plan and save it; that closes the task.",
            "Amber: he read it before your last change. He is asked again on his next look.",
        ],
        shots: [
            {
                src: "/help/tech-plan/office-reads.png",
                alt: "Who read the plan, under What the tech will do",
                marks: [
                    { x: 25, y: 64, label: "Red: the tech says it looks wrong", side: "above" },
                    { x: 67, y: 64, label: "Green: read, and when", side: "above" },
                ],
            },
        ],
    },
};
